import { CSSProperties, FC } from "react";
import { TooltipGrid, TooltipGridCell, TooltipGridRow } from "@infobis/ui-styles-utils";
import { max, min } from "lodash";
import { differenceInCalendarDays } from "date-fns";
import { formatDate } from "@utils/format/date";
import stBase from '../baseTooltipStyles.module.scss';
import { declineDays } from "@utils/highchart/tooltip";
import { formatNumber2d } from "@utils/format/number";
import { ChartCircle } from "../Circle/ChartCircle";
import { TooltipGridProps } from "@infobis/ui-styles-utils/components/TooltipGrid/TooltipGrid";
import {
    NdviHeader,
    PhasesHeader, WorksHeader
} from "@modules/dashboards/dashboardTooltips/TooltipVariants/BaseChartTooltipWithHeader/headers/headers";
import { IPeriod } from '@baseTypes/service/dashboard/IWorksDashboardService'

export enum HeaderTypes {
    phases = 'phases',
    works = 'works',
    ndvi = 'ndvi'
}

interface Element {
    name: string
    color: string
    dateStart?: number
    dateEnd?: number
    id: number | string
    value?: string | number
}

interface CropsWithHeaderTooltipProps {
    header: {
        type: HeaderTypes,
        title?: string
    } & Partial<IPeriod>,
    elements: Element[]
    tooltipProps?: TooltipGridProps['tooltipProps']
    content?: TooltipGridProps['content']
    className?: string
    width?: number | string
    style?: CSSProperties
    cellsInRow?: TooltipGridProps['cellsInRow']
}

const BaseChartTooltipWithHeader: FC<CropsWithHeaderTooltipProps> = (props) => {
    const {
        header,
        content,
        elements,
        tooltipProps,
        className = '',
        width,
        style = {},
        cellsInRow
    } = props;

    const renderHeader = () => {
        const dateStart = header.from || min(elements.map(element => element.dateStart))!;
        const dateEnd = header.to || max(elements.map(element => element.dateEnd))!;

        if (header.type === HeaderTypes.phases) {
            return (
                <PhasesHeader title={header.title} dateStart={formatDate(dateStart)} dateEnd={formatDate(dateEnd)} />
            )
        }

        if (header.type === HeaderTypes.works) {
            const differenceInDays = Math.abs(differenceInCalendarDays(dateStart, dateEnd)) + 1;
            return (
                <WorksHeader
                    dateStart={formatDate(dateStart)}
                    dateEnd={formatDate(dateEnd)}
                    differenceInDays={declineDays(differenceInDays, formatNumber2d)}
                />
            )
        }

        if (header.type === HeaderTypes.ndvi) return <NdviHeader title={header.title}/>

        return <TooltipGridRow children={[]}/>
    }

    const renderBody = () => {
        return (
            <>
                {
                    elements.map(element => {
                        return (
                            <TooltipGridRow key={element.id}>
                                <TooltipGridCell contentClassName={stBase['cell-content']}>
                                    <ChartCircle color={element.color} text={element.name} />
                                </TooltipGridCell>
                                <TooltipGridCell align={'end'}>
                                    {header.type === HeaderTypes.ndvi
                                        ? element.value
                                        : formatDate(element.dateStart) + ' - ' + formatDate(element.dateEnd)}
                                </TooltipGridCell>
                            </TooltipGridRow>
                        )
                    })
                }
            </>
        )
    }

    return (
        <TooltipGrid
            style={{ width: width, ...style }}
            ellipsis={false}
            tooltipProps={{
                hideOnAnywhereClick: false,
                trigger: 'static',
                ...tooltipProps
            }}
            content={content}
            className={className}
            cellsInRow={cellsInRow}
        >
            {renderHeader()}
            {renderBody()}
        </TooltipGrid>
    )
}

export default BaseChartTooltipWithHeader;